<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import type { LinkCardData, OgpData } from '$lib/api/types';

  export let data: LinkCardData;
  export let ogp: OgpData | undefined;
  export let editable = false;
  export let hovered = false;

  const dispatch = createEventDispatcher<{
    change: LinkCardData;
  }>();

  let editing = false;
  let editUrl = data.url;
  let editComment = data.comment || '';

  function handleClick() {
    if (!editable) {
      window.open(data.url, '_blank', 'noopener,noreferrer');
    }
  }

  function startEditing(event: MouseEvent) {
    event.stopPropagation();
    editUrl = data.url;
    editComment = data.comment || '';
    editing = true;
  }

  function handleSave() {
    const newComment = editComment.trim() || null;
    if (editUrl !== data.url || newComment !== (data.comment || null)) {
      dispatch('change', { ...data, url: editUrl, comment: newComment });
    }
    editing = false;
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      handleSave();
    }
    if (event.key === 'Escape') {
      editing = false;
    }
  }

  // Get display title
  $: displayTitle = data.titleOverride || ogp?.title || extractDomain(data.url);
  $: displayDesc = data.descOverride || ogp?.description;

  function extractDomain(url: string): string {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  }
</script>

<div
  class="h-full p-4 flex flex-col cursor-pointer hover:bg-gray-50 transition-colors"
  on:click={handleClick}
  role="link"
  tabindex="0"
>
  {#if editing}
    <div class="flex-1 flex flex-col gap-2 relative z-30" on:click|stopPropagation>
      <input
        type="url"
        bind:value={editUrl}
        placeholder="URLを入力"
        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
        on:keydown={handleKeydown}
      />
      <input
        type="text"
        bind:value={editComment}
        placeholder="ひとこと（任意）"
        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
        on:keydown={handleKeydown}
      />
      <div class="flex justify-between items-center">
        <p class="text-xs text-gray-500">Enterで保存</p>
        <button
          on:click={handleSave}
          class="px-3 py-1 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600"
        >
          保存
        </button>
      </div>
    </div>
  {:else}
    <!-- OGP Image -->
    {#if ogp?.imageUrl}
      <div class="w-full h-24 mb-3 rounded-lg overflow-hidden bg-gray-100">
        <img
          src={ogp.imageUrl}
          alt=""
          class="w-full h-full object-cover"
        />
      </div>
    {/if}

    <!-- Content -->
    <div class="flex-1 flex flex-col">
      <div class="flex items-center gap-2 mb-1">
        {#if ogp?.favicon}
          <img src={ogp.favicon} alt="" class="w-4 h-4" />
        {:else}
          <i class="lni lni-link-2-angular-right text-gray-400"></i>
        {/if}
        <h3 class="font-medium text-gray-900 truncate flex-1">
          {displayTitle}
        </h3>
      </div>

      {#if data.comment}
        <p class="text-sm text-purple-600 mb-1">{data.comment}</p>
      {/if}

      {#if displayDesc}
        <p class="text-sm text-gray-600 truncate-2">{displayDesc}</p>
      {/if}

      <p class="mt-auto text-xs text-gray-400 truncate">
        {extractDomain(data.url)}
      </p>
    </div>

    <!-- Edit Button -->
    {#if editable && hovered}
      <button
        on:click={startEditing}
        class="absolute top-2 right-2 z-30 text-gray-400 hover:text-gray-600 text-xs"
      >
        編集
      </button>
    {/if}
  {/if}
</div>
